export * from "./model";

import { fromIso8601DateTime } from "@/shared/lib/datetime";
import type { FreightMatching } from "@x-mile/api-contract";
import { isBefore } from "date-fns";
import { calculateDeliveryProposalResponseDeadlineAt } from "./delivery-proposal-response-deadline-at";
import type { OfferCargoProcess } from "./model";

// 配送提案が「有効」か
export const isDeliveryProposalValid = (
  offerCargoProcess: OfferCargoProcess,
  deliveryProposalOfferCargo: FreightMatching.schemas.OfferCargo,
): boolean => {
  return (
    // 荷物と異なるcompany
    !!deliveryProposalOfferCargo.company &&
    !!offerCargoProcess.draftOfferCargo.company &&
    deliveryProposalOfferCargo.company.id !==
      offerCargoProcess.draftOfferCargo.company.id &&
    // 荷物と同じprocess
    offerCargoProcess.draftOfferCargo.process.id ===
      deliveryProposalOfferCargo.process.id
  );
};

// 配送提案が「進行中」か
export const isDeliveryProposalOnGoing = (
  deliveryProposalOfferCargo: FreightMatching.schemas.OfferCargo,
): boolean => {
  const now = new Date();
  const responseDeadlineAtDate = calculateDeliveryProposalResponseDeadlineAt(
    deliveryProposalOfferCargo,
  );
  return (
    // 見送られていない
    deliveryProposalOfferCargo.rejection === undefined &&
    // 提案受付期限が過ぎていない
    isBefore(now, responseDeadlineAtDate)
  );
};

// 契約が「有効」か
export const isContractValid = (
  offerCargoProcess: OfferCargoProcess,
  contractOfferCargo: FreightMatching.schemas.OfferCargo,
): boolean => {
  return (
    // 荷物と同じcompany
    !!contractOfferCargo.company &&
    !!offerCargoProcess.draftOfferCargo.company &&
    contractOfferCargo.company.id ===
      offerCargoProcess.draftOfferCargo.company.id &&
    // 荷物と同じprocess
    offerCargoProcess.draftOfferCargo.process.id ===
      contractOfferCargo.process.id
  );
};
// 契約が「合意済み」か
export const isContractConcluded = (
  contractOfferCargo: FreightMatching.schemas.OfferCargo,
): boolean => {
  return (
    // 合意済み
    contractOfferCargo.stage === "contract_concluded" &&
    // 見送られていない
    contractOfferCargo.rejection === undefined
  );
};

export type OfferCargoProcessStatusParams = {
  process: OfferCargoProcess;
};
export type OfferCargoProcessStatus =
  | "no_valid_delivery_proposals" // 有効かつ進行中の配送提案が１つも無い
  | "reception_suspended" // 受付停止されている
  | "has_ongoing_proposal_with_no_contract" // 有効かつ進行中の配送提案で契約が1つもないものがある
  | "has_contracts_all_rejected" // 有効かつ進行中の配送提案にひもづく有効な契約が存在するが、全て見送られている
  | "has_contract_pending" // 有効かつ進行中の配送提案にひもづく、有効だが合意も見送りもされていない契約が存在する
  | "proposal_reception_deadline_expired" // 有効かつ進行中の配送提案にひもづく、有効かつ合意はひとつもなく、荷物の提示期限が過ぎている
  | "has_contract_concluded"; // 有効かつ進行中の配送提案にひもづく、有効かつ合意済みの契約が存在する
export const calculateOfferCargoProcessStatus = ({
  process,
}: OfferCargoProcessStatusParams) => {
  const now = new Date();
  // 有効な配送提案
  const validDeliveryProposals = process.deliveryProposalOfferCargos.filter(
    (proposal) => isDeliveryProposalValid(process, proposal),
  );
  // 有効かつ進行中の配送提案
  const validOnGoingDeliveryProposals =
    process.deliveryProposalOfferCargos.filter(
      (proposal) =>
        isDeliveryProposalValid(process, proposal) &&
        isDeliveryProposalOnGoing(proposal),
    );
  // 有効な配送提案にひもづく「有効な契約」
  const validContracts = process.contractOfferCargos.filter((contract) => {
    if (!isContractValid(process, contract)) {
      return false;
    }
    return validDeliveryProposals.some(
      (proposal) => proposal.id === contract.sourceOfferCargoId,
    );
  });
  // 有効かつ進行中の配送提案にひもづく「有効な契約」
  const validOnGoingContracts = validContracts.filter((contract) =>
    validOnGoingDeliveryProposals.some(
      (proposal) => proposal.id === contract.sourceOfferCargoId,
    ),
  );
  // 合意済みの契約
  const concludedContracts = validContracts.filter((contract) =>
    isContractConcluded(contract),
  );
  // 有効だが見送られている契約
  const validButRejectedContracts = validContracts.filter(
    (contract) => !!contract.rejection,
  );

  // 「荷物の提案受付期限(deadlineAt)」が過ぎているかどうか
  const processReceptionDeadlineAtDate = fromIso8601DateTime(
    process.draftOfferCargo.deadlineAt,
  );
  const isDeadlineExpired = isBefore(processReceptionDeadlineAtDate, now);

  //
  // 条件判定
  //
  // 受付停止されている場合 => "reception_suspended"
  if (process.draftOfferCargo.process.isSuspended) {
    return "reception_suspended";
  }

  // 合意済みの契約がある場合 => "has_contract_concluded"
  if (concludedContracts.length > 0) {
    return "has_contract_concluded";
  }

  // 合意済み契約がない状態で、提示期限が過ぎている場合 => "proposal_reception_deadline_expired"
  if (isDeadlineExpired) {
    return "proposal_reception_deadline_expired";
  }

  // 有効かつ進行中の配送提案が一つもない場合 => "no_valid_delivery_proposals"
  if (validOnGoingDeliveryProposals.length === 0) {
    return "no_valid_delivery_proposals";
  }

  // 有効な契約が一つもない場合 => "has_ongoing_proposal_with_no_contract"
  if (validOnGoingContracts.length === 0) {
    return "has_ongoing_proposal_with_no_contract";
  }

  // 有効な契約が「すべて見送り(rejection が設定)」されている場合 => "has_contracts_all_rejected"
  if (validButRejectedContracts.length === validContracts.length) {
    return "has_contracts_all_rejected";
  }

  // 「まだ合意もなく、かつ全て見送りでもない」＝「保留(応答待ち)の契約がある」 => "has_contract_pending"
  return "has_contract_pending";
};

export type DeliveryProposalOfferCargoStatusParams = {
  deliveryProposalOfferCargoId: number;
  process: OfferCargoProcess;
};
export type DeliveryProposalOfferCargoStatus =
  | "no_valid_contracts" // この配送提案に紐づいている有効な契約が一つもない
  | "delivery_proposal_rejected" // この配送提案が見送られている
  | "has_contracts_all_rejected" // この配送提案は見送られておらず、紐づいている有効な契約が存在するが、全て見送られている
  | "has_contract_pending" // この配送提案は見送られておらず、紐づいている有効だが見送りも合意もされていない契約が存在する
  | "delivery_proposal_response_deadline_expired" // この配送提案が作成から24h以上経過している(返答期限が過ぎている)
  | "has_contract_concluded"; // この配送提案は見送られておらず、紐づいている合意された契約が存在する

export const calculateDeliveryProposalOfferCargoStatus = (
  params: DeliveryProposalOfferCargoStatusParams,
): DeliveryProposalOfferCargoStatus => {
  const { process, deliveryProposalOfferCargoId } = params;

  // この配送提案
  const deliveryProposalOfferCargo = process.deliveryProposalOfferCargos.find(
    (cargo) => cargo.id === deliveryProposalOfferCargoId,
  );
  if (!deliveryProposalOfferCargo) {
    throw new Error(
      `DeliveryProposalOfferCargo not found. id: ${deliveryProposalOfferCargoId}`,
    );
  }

  const now = new Date();
  const responseDeadlineAtDate = calculateDeliveryProposalResponseDeadlineAt(
    deliveryProposalOfferCargo,
  );

  // この配送提案に紐づく「有効な契約」
  const associatedValidContracts = process.contractOfferCargos.filter(
    (contract) =>
      isContractValid(process, contract) &&
      contract.sourceOfferCargoId === deliveryProposalOfferCargo.id,
  );

  // この配送提案に紐づく「合意済みの契約」
  const contractConculuedOfferCargos = associatedValidContracts.filter(
    (contract) => isContractConcluded(contract),
  );

  // 提案自体が見送られているか
  if (deliveryProposalOfferCargo.rejection !== undefined) {
    return "delivery_proposal_rejected";
  }

  // 合意した契約があるか
  if (contractConculuedOfferCargos.length > 0) {
    return "has_contract_concluded";
  }

  // 提案の返答期限が切れているか
  //  (この例では「作成から24時間を過ぎていたら期限切れ」とする)
  if (now.getTime() > responseDeadlineAtDate.getTime()) {
    return "delivery_proposal_response_deadline_expired";
  }

  // 有効な契約が1つもなければ
  if (associatedValidContracts.length === 0) {
    return "no_valid_contracts";
  }

  // 紐づく契約がすべて見送りになっているか
  const allRejected = associatedValidContracts.every(
    (contract) => contract.rejection !== undefined,
  );
  if (allRejected) {
    return "has_contracts_all_rejected";
  }

  // ここまで該当なし → 「まだ見送りでも合意でもない契約がある」 = "has_contract_pending"
  return "has_contract_pending";
};

export const calculateLatestOnGoingDeliveryProposalOfferCargo = (args: {
  offerCargoProcess: OfferCargoProcess;
}): FreightMatching.schemas.OfferCargo | undefined => {
  const { offerCargoProcess } = args;
  const validOnGoingDeliveryProposals =
    offerCargoProcess.deliveryProposalOfferCargos
      .filter(
        (proposal) =>
          isDeliveryProposalValid(offerCargoProcess, proposal) &&
          isDeliveryProposalOnGoing(proposal),
      )
      .sort((a, b) => {
        const aCreatedAtDateTime = fromIso8601DateTime(a.createdAt);
        const bCreatedAtDateTime = fromIso8601DateTime(b.createdAt);
        return aCreatedAtDateTime.getTime() - bCreatedAtDateTime.getTime();
      });
  return validOnGoingDeliveryProposals[0];
};

export const calculateLatestValidContractOfferCargo = (args: {
  deliveryProposalOfferCargoId: number;
  offerCargoProcess: OfferCargoProcess;
}): FreightMatching.schemas.OfferCargo | undefined => {
  const { deliveryProposalOfferCargoId, offerCargoProcess } = args;

  const deliveryProposalOfferCargo =
    offerCargoProcess.deliveryProposalOfferCargos.find(
      (cargo) => cargo.id === deliveryProposalOfferCargoId,
    );
  if (!deliveryProposalOfferCargo) {
    throw new Error(
      `deliveryProposalOfferCargoIdが存在しません。id: ${deliveryProposalOfferCargoId}`,
    );
  }
  const validContracts = offerCargoProcess.contractOfferCargos
    .filter(
      (contract) =>
        isContractValid(offerCargoProcess, contract) &&
        contract.sourceOfferCargoId === deliveryProposalOfferCargo.id,
    )
    .sort((a, b) => {
      const aCreatedAtDateTime = fromIso8601DateTime(a.createdAt);
      const bCreatedAtDateTime = fromIso8601DateTime(b.createdAt);
      return aCreatedAtDateTime.getTime() - bCreatedAtDateTime.getTime();
    });
  return validContracts[0];
};
